import { useCallback, useRef, useState } from 'react'
import { Album } from '@/models/album.ts'
import { useClient } from '@/stores/useClient.ts'
import { useAlbums } from '@/stores/useAlbums.ts'

/**
 * Merges the server's reorder response into the albums the caller already holds.
 *
 * The response is the plain album serializer, so it lacks fields some views add on
 * top (e.g. `files_count` on prof projects). Spreading the response over the known
 * object keeps those and still takes the updated `order` / `is_primary`.
 */
export function mergeReorderedAlbums<T extends Album>(known: T[], response: Album[]): T[] {
  const byId = new Map(known.map(a => [a.id, a]))
  return response.map(a => ({ ...(byId.get(a.id) ?? {}), ...a }) as T)
}

/** Returns a copy of `albums` with the album at `from` moved to `to`. */
export function moveAlbumInList<T>(albums: T[], from: number, to: number): T[] {
  const next = [...albums]
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)
  return next
}

interface UseAlbumReorderOptions<T extends Album> {
  driveId: string
  albums: T[]
  /** Writes the (optimistic or confirmed) order into whatever holds the albums. */
  applyAlbums: (albums: T[]) => void
  onError?: (error: unknown) => void
  /** Called once the server confirmed the new order. */
  onSuccess?: (albums: T[]) => void
}

/**
 * Optimistic album reordering shared by the B2C settings and the portal.
 *
 * `moveAlbum` shifts one album by `delta` positions, `reorder` takes a complete
 * new list. Both write the new order immediately via `applyAlbums`, send ALL album
 * ids to the server and, on failure, restore the order from before the move. The
 * album at index 0 becomes the primary album – that is decided by the server, the
 * response (with `order` and `is_primary` updated) is what ends up in state.
 */
export default function useAlbumReorder<T extends Album>({
  driveId,
  albums,
  applyAlbums,
  onError,
  onSuccess,
}: UseAlbumReorderOptions<T>) {
  const { reorderAlbums } = useClient()
  const { setAlbum } = useAlbums()
  const [isReordering, setIsReordering] = useState(false)
  // Latest list, so a second move while a request is in flight builds on the
  // optimistic state and not on a stale closure.
  const albumsRef = useRef(albums)
  albumsRef.current = albums

  const reorder = useCallback(async (nextAlbums: T[]) => {
    const previous = albumsRef.current
    applyAlbums(nextAlbums)
    setIsReordering(true)
    try {
      const res = await reorderAlbums(driveId, nextAlbums.map(a => a.id))
      const confirmed = mergeReorderedAlbums(nextAlbums, res.data)
      applyAlbums(confirmed)
      for (const album of confirmed) {
        setAlbum(album)
      }
      onSuccess?.(confirmed)
    } catch (e) {
      console.error('error reordering albums', e)
      applyAlbums(previous)
      onError?.(e)
    } finally {
      setIsReordering(false)
    }
  }, [driveId, applyAlbums, reorderAlbums, setAlbum, onError, onSuccess])

  const moveAlbum = useCallback((albumId: string, delta: number) => {
    const current = albumsRef.current
    const from = current.findIndex(a => a.id === albumId)
    if (from === -1) return
    const to = Math.max(0, Math.min(current.length - 1, from + delta))
    if (to === from) return
    return reorder(moveAlbumInList(current, from, to))
  }, [reorder])

  return { reorder, moveAlbum, isReordering }
}
